/**
 * @fileoverview CrmActionsBar — three-button bulk-action bar for the
 * SuperAdmin → Operations → CRM subtab.
 *
 * Renders Backfill (primary), Reconcile (secondary), and Cleanup orphans
 * (danger) buttons in a horizontal row. Cleanup is guarded by a
 * ConfirmModal so an operator can't nuke orphan mappings with a single
 * misclick. This component is presentational — the parent owns the async
 * work, decides which action is currently running, and dispatches any
 * post-completion toast.
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import ConfirmModal from './ConfirmModal';

/**
 * Bulk-action bar for CRM operator jobs (Backfill / Reconcile / Cleanup).
 *
 * Every button is disabled whenever any action is currently running OR
 * when the `disabled` prop is true (e.g. CRM_ENABLED is false). While
 * `running` matches a button's key, that button shows a spinner and its
 * label swaps to "Running...". The Cleanup button opens a ConfirmModal
 * before invoking `onCleanup`; the confirm message includes `orphanCount`
 * when provided.
 *
 * @param {Object} props
 * @param {Function} props.onBackfill - Handler invoked when Backfill is clicked
 * @param {Function} props.onReconcile - Handler invoked when Reconcile is clicked
 * @param {Function} props.onCleanup - Handler invoked when Cleanup is confirmed
 * @param {?('backfill'|'reconcile'|'cleanup')} props.running - Currently running action, or null
 * @param {boolean} [props.disabled=false] - When true, every button is disabled
 * @param {?number} [props.orphanCount] - Count of orphan mappings shown in the confirm dialog
 * @returns {JSX.Element}
 */
function CrmActionsBar({
  onBackfill,
  onReconcile,
  onCleanup,
  running = null,
  disabled = false,
  orphanCount = null,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const anyRunning = running === 'backfill' || running === 'reconcile' || running === 'cleanup';
  const allDisabled = !!disabled || anyRunning;

  /**
   * Open the cleanup confirm dialog. No-op when the bar is disabled or
   * another action is already running.
   */
  const handleCleanupClick = useCallback(() => {
    if (allDisabled) return;
    setConfirmOpen(true);
  }, [allDisabled]);

  /**
   * Confirm dialog callback: close the dialog and fire the parent's
   * onCleanup handler. The parent is responsible for flipping `running`
   * to 'cleanup' and toasting the result.
   */
  const handleConfirmCleanup = useCallback(() => {
    setConfirmOpen(false);
    if (onCleanup) onCleanup();
  }, [onCleanup]);

  /**
   * Confirm dialog cancel: dismiss without invoking onCleanup.
   */
  const handleCancelCleanup = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const cleanupMessage = typeof orphanCount === 'number'
    ? `This will remove ${orphanCount} orphaned CRM records. Continue?`
    : 'Continue?';

  return (
    <div className="crm-actions-bar" role="group" aria-label="CRM bulk actions">
      <button
        type="button"
        className={`btn btn-primary${running === 'backfill' ? ' btn-loading' : ''}`}
        onClick={onBackfill}
        disabled={allDisabled}
        aria-label="Backfill CRM records"
        data-testid="crm-actions-backfill"
      >
        {running === 'backfill' ? 'Running...' : 'Backfill'}
      </button>
      <button
        type="button"
        className={`btn btn-secondary${running === 'reconcile' ? ' btn-loading' : ''}`}
        onClick={onReconcile}
        disabled={allDisabled}
        aria-label="Reconcile CRM records"
        data-testid="crm-actions-reconcile"
      >
        {running === 'reconcile' ? 'Running...' : 'Reconcile'}
      </button>
      <button
        type="button"
        className={`btn btn-danger${running === 'cleanup' ? ' btn-loading' : ''}`}
        onClick={handleCleanupClick}
        disabled={allDisabled}
        aria-label="Cleanup orphaned CRM records"
        data-testid="crm-actions-cleanup"
      >
        {running === 'cleanup' ? 'Running...' : 'Cleanup orphans'}
      </button>

      <ConfirmModal
        isOpen={confirmOpen}
        onConfirm={handleConfirmCleanup}
        onCancel={handleCancelCleanup}
        title="Cleanup orphaned CRM records"
        message={cleanupMessage}
        confirmLabel="Cleanup"
        cancelLabel="Cancel"
        variant="danger"
      />
    </div>
  );
}

CrmActionsBar.propTypes = {
  /** Handler invoked when Backfill is clicked */
  onBackfill: PropTypes.func.isRequired,
  /** Handler invoked when Reconcile is clicked */
  onReconcile: PropTypes.func.isRequired,
  /** Handler invoked when Cleanup is confirmed */
  onCleanup: PropTypes.func.isRequired,
  /** Currently running action, or null if none */
  running: PropTypes.oneOf(['backfill', 'reconcile', 'cleanup', null]),
  /** When true, every button is disabled regardless of running */
  disabled: PropTypes.bool,
  /** Optional count of orphan mappings, shown in the confirm dialog */
  orphanCount: PropTypes.number,
};

export default CrmActionsBar;
